import { useState, useEffect } from 'react';
import api from '../api';

const ROLES = ['Admin', 'Leadership', 'Senior Project Executive', 'Project Manager'];

export default function UsersAdminPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState('');

  const loadUsers = () => {
    api.getUsers().then(setUsers).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(loadUsers, []);

  const startEdit = (u) => {
    setEditId(u.UserId);
    setEditForm({ displayName: u.DisplayName, email: u.Email, role: u.Role, isAdmin: u.IsAdmin, isActive: u.IsActive });
    setError('');
  };

  const saveEdit = async () => {
    try {
      await api.updateUser(editId, editForm);
      setEditId(null);
      loadUsers();
    } catch (err) { setError(err.message); }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Disable user "${user.DisplayName}"? They will no longer be able to log in.`)) return;
    try {
      await api.updateUser(user.UserId, {
        displayName: user.DisplayName,
        role: user.Role,
        isAdmin: user.IsAdmin,
        isActive: false,
      });
      setEditId(null);
      loadUsers();
    } catch (err) { setError(err.message); }
  };

  const resetPassword = async (id) => {
    const pw = window.prompt('Enter temporary password (min 8 chars, upper+lower+digit+special):');
    if (!pw) return;
    try {
      await api.resetUserPassword(id, pw);
      alert('Password reset. User will be prompted to change on next login.');
    } catch (err) { alert(err.message); }
  };

  if (loading) return <div className="page-loading">Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h1>User Management</h1>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => { setShowCreate(true); setEditId(null); }}>Add User</button>
        </div>
      </div>

      {showCreate && <CreateUserForm onDone={() => { setShowCreate(false); loadUsers(); }} onCancel={() => setShowCreate(false)} />}

      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Admin</th>
              <th>Status</th>
              <th>Last Login</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              editId === u.UserId ? (
                <tr key={u.UserId} className="editing-row">
                  <td><input value={editForm.displayName} onChange={e => setEditForm({ ...editForm, displayName: e.target.value })} className="inline-input" /></td>
                  <td><input value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="inline-input" /></td>
                  <td>
                    <select value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })} className="inline-select">
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td>
                    <input type="checkbox" checked={editForm.isAdmin} onChange={e => setEditForm({ ...editForm, isAdmin: e.target.checked })} />
                  </td>
                  <td>
                    <select value={editForm.isActive ? '1' : '0'} onChange={e => setEditForm({ ...editForm, isActive: e.target.value === '1' })} className="inline-select">
                      <option value="1">Active</option>
                      <option value="0">Disabled</option>
                    </select>
                  </td>
                  <td>{u.LastLoginAt ? new Date(u.LastLoginAt).toLocaleDateString() : 'Never'}</td>
                  <td className="action-cell">
                    <button className="btn btn-primary btn-sm" onClick={saveEdit}>Save</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditId(null)}>Cancel</button>
                  </td>
                </tr>
              ) : (
                <tr key={u.UserId} className={!u.IsActive ? 'disabled-row' : ''}>
                  <td>{u.DisplayName}</td>
                  <td>{u.Email}</td>
                  <td>{u.Role}</td>
                  <td>{u.IsAdmin ? 'Yes' : ''}</td>
                  <td><span className={`status-badge ${u.IsActive ? 'active' : 'disabled'}`}>{u.IsActive ? 'Active' : 'Disabled'}</span></td>
                  <td>{u.LastLoginAt ? new Date(u.LastLoginAt).toLocaleDateString() : 'Never'}</td>
                  <td className="action-cell">
                    <button className="btn btn-ghost btn-sm" onClick={() => startEdit(u)}>Edit</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => resetPassword(u.UserId)}>Reset PW</button>
                    <button className="btn btn-ghost btn-sm btn-danger" onClick={() => deleteUser(u)}>Disable</button>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreateUserForm({ onDone, onCancel }) {
  const [form, setForm] = useState({ email: '', displayName: '', role: 'Project Manager', isAdmin: false, password: '' });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createUser(form);
      onDone();
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="inline-form-card">
      <h3>Create User</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-field">
            <label>Display Name</label>
            <input value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} required />
          </div>
          <div className="form-field">
            <label>Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
          </div>
          <div className="form-field">
            <label>Role</label>
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Temp Password</label>
            <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
          </div>
        </div>
        {error && <div className="form-error">{error}</div>}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary">Create</button>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
